import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, shortDate, useToast } from '../components/ui';
import type { HouseholdAccess, Language } from '../types';

const T = {
  en: {
    title: 'Who can sign in',
    intro:
      'Everyone here shares one household — the same week, the same shopping list, the same meals. There are no passwords: signing in sends a link to the address.',
    invite: 'Invite someone',
    emailPlaceholder: 'their@email.address',
    send: 'Send invitation',
    sent: (email: string) => `Invitation sent to ${email}`,
    pending: 'Invited, not signed in yet',
    expires: 'expires',
    cancel: 'Cancel',
    cancelled: 'Invitation withdrawn',
    remove: 'Remove',
    removed: 'Access removed',
    owner: 'Owner',
    adult: 'Adult',
    aupair: 'Au pair',
    you: 'you',
    confirmRemove: (email: string) => `Remove ${email}? They will be signed out and lose access.`,
    ownerNote: 'The owner account cannot be removed.',
    inviteNote:
      'An invitation is valid for 14 days. They get full adult access — settings, planning and shopping.',
    planner: 'Meal planner',
    plannerTitle:
      'Gets an email the day before a shop if the week still is not planned',
    plannerNote:
      'Meal planners are emailed the day before a shopping trip if the week has not been planned — the one thing that cannot be fixed after the shop. If nobody is ticked, the reminder goes to the owner.',
    plannerOn: (email: string) => `${email} will get the planning reminder`,
    plannerOff: (email: string) => `${email} will no longer get the planning reminder`,
  },
  fr: {
    title: 'Qui peut se connecter',
    intro:
      'Tout le monde ici partage le même foyer — la même semaine, la même liste de courses, les mêmes repas. Pas de mot de passe : la connexion envoie un lien à l’adresse.',
    invite: 'Inviter quelqu’un',
    emailPlaceholder: 'son@adresse.email',
    send: 'Envoyer l’invitation',
    sent: (email: string) => `Invitation envoyée à ${email}`,
    pending: 'Invité, pas encore connecté',
    expires: 'expire le',
    cancel: 'Annuler',
    cancelled: 'Invitation annulée',
    remove: 'Retirer',
    removed: 'Accès retiré',
    owner: 'Propriétaire',
    adult: 'Adulte',
    aupair: 'Au pair',
    you: 'vous',
    confirmRemove: (email: string) => `Retirer ${email} ? La personne sera déconnectée.`,
    ownerNote: 'Le compte propriétaire ne peut pas être retiré.',
    inviteNote:
      'Une invitation est valable 14 jours. La personne obtient un accès adulte complet.',
    planner: 'Planificateur',
    plannerTitle:
      'Reçoit un e-mail la veille des courses si la semaine n’est toujours pas planifiée',
    plannerNote:
      'Les planificateurs reçoivent un e-mail la veille des courses si la semaine n’est pas planifiée. Si personne n’est coché, le rappel va au propriétaire.',
    plannerOn: (email: string) => `${email} recevra le rappel`,
    plannerOff: (email: string) => `${email} ne recevra plus le rappel`,
  },
} as const;

/**
 * Household access (§7).
 *
 * The household is the unit, not the person: an invitation is not "share this
 * plan with someone", it is "this is your kitchen too". So this deliberately
 * sits next to the people list rather than being hidden in an account menu.
 */
export function HouseholdAccessCard({
  language,
  currentEmail,
}: {
  language: Language;
  currentEmail?: string;
}) {
  const t = T[language];
  const toast = useToast();
  const [access, setAccess] = useState<HouseholdAccess | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setAccess(await api.access());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!access) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  const roleLabel = (role: string) =>
    role === 'owner' ? t.owner : role === 'aupair' ? t.aupair : t.adult;

  const run = async (action: () => Promise<HouseholdAccess>, message: string) => {
    setBusy(true);
    try {
      setAccess(await action());
      toast(message);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not do that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 14 }}>
        {t.intro}
      </p>

      <div className="stack" style={{ gap: 8 }}>
        {access.users.map((user) => (
          <div className="access-row" key={user.id}>
            <span className="access-email">
              {user.email}
              {user.email.toLowerCase() === currentEmail?.toLowerCase() && (
                <span className="dim"> ({t.you})</span>
              )}
            </span>
            <span className="pill">{roleLabel(user.role)}</span>
            <label className="row" style={{ gap: 6 }} title={t.plannerTitle}>
              <input
                type="checkbox"
                style={{ width: 20, height: 20, minHeight: 0 }}
                checked={user.is_meal_planner}
                disabled={busy}
                onChange={(event) =>
                  void run(
                    () => api.setMealPlanner(user.id, event.target.checked),
                    event.target.checked ? t.plannerOn(user.email) : t.plannerOff(user.email),
                  )
                }
              />
              <span className="dim">{t.planner}</span>
            </label>
            {user.role !== 'owner' && (
              <button
                className="btn small danger"
                disabled={busy}
                onClick={() => {
                  if (!confirm(t.confirmRemove(user.email))) return;
                  void run(() => api.removeAccess(user.id), t.removed);
                }}
              >
                {t.remove}
              </button>
            )}
          </div>
        ))}

        {access.pending.map((invite) => (
          <div className="access-row pending" key={invite.email}>
            <span className="access-email">{invite.email}</span>
            <span className="pill open">
              {t.pending} · {t.expires} {shortDate(invite.expires_at.slice(0, 10), language)}
            </span>
            <button
              className="btn small"
              disabled={busy}
              onClick={() => void run(() => api.cancelInvite(invite.email), t.cancelled)}
            >
              {t.cancel}
            </button>
          </div>
        ))}
      </div>

      <form
        className="row"
        style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}
        onSubmit={async (event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const email = String(new FormData(form).get('email') ?? '').trim();
          if (!email) return;
          setBusy(true);
          try {
            const result = await api.invite(email);
            setAccess(result.access);
            form.reset();
            toast(t.sent(email));
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not send that invitation');
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          name="email"
          type="email"
          required
          placeholder={t.emailPlaceholder}
          style={{ flex: 2, minWidth: 200 }}
          disabled={busy}
        />
        <button className="btn primary" type="submit" disabled={busy}>
          {t.send}
        </button>
      </form>

      <p className="dim" style={{ fontSize: 13, marginTop: 10 }}>
        {t.inviteNote}
      </p>
      <p className="dim" style={{ fontSize: 13, marginTop: 8 }}>
        {t.plannerNote}
      </p>
    </Card>
  );
}
